// src/pages/ToastTestPage.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Paper,
  Divider,
  Chip,
  ButtonGroup,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Refresh,
  Timer,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { colors } from '../theme/theme';
import ToastMessage from '../components/common/ToastMessage';
import {
  SAMPLE_MESSAGES,
  SAMPLE_ERROR_MESSAGES,
  TimingConfig,
  calculateTotalTime,
  formatTime,
  formatTimeBreakdown,
} from '../utils/toastTestUtils';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: '1400px',
  margin: '0 auto',
  padding: '2rem',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(30, 32, 36, 0.8)'
      : 'rgba(248, 250, 252, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  minHeight: 'calc(100vh - 4rem)',
}));

const PageHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '2rem',
  padding: '2rem',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  borderRadius: '16px',
  color: 'white',
}));

const ControlCard = styled(Card)(() => ({
  marginBottom: '1.5rem',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${colors.primary.light}`,
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: '1rem',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.8)',
  borderRadius: '8px',
  marginBottom: '1rem',
}));

const ToastTestPage: React.FC = () => {
  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toastMessage, setToastMessage] = useState('');

  // Control state
  const [infiniteLoop, setInfiniteLoop] = useState(false);
  const [loopCount, setLoopCount] = useState(0);
  const [customMessage, setCustomMessage] = useState('');

  // Timing state - start with current/default preset
  const [timing] = useState<TimingConfig>({
    scaleInDuration: 0.5,
    iconDrawDuration: 0.3,
    iconDrawDelay: 0.4,
    holdDuration: 0.5,
    fadeOutDuration: 0.1,
  });

  // Trigger toast
  const triggerToast = (message?: string) => {
    setShowToast(false);
    setTimeout(() => {
      if (message) {
        setToastMessage(message);
      }
      setShowToast(true);
      setLoopCount((prev) => prev + 1);
    }, 100);
  };

  // Handle toast close
  const handleToastClose = () => {
    setShowToast(false);
    if (infiniteLoop) {
      // Restart after a brief pause
      setTimeout(() => {
        triggerToast();
      }, 500);
    }
  };

  // Reset to defaults
  const resetToDefaults = () => {
    setInfiniteLoop(false);
    setLoopCount(0);
    setToastMessage('');
    setToastType('success');
    setCustomMessage('');
  };

  // Calculate total time
  const totalTime = calculateTotalTime(timing);

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          <Timer sx={{ fontSize: '3rem', verticalAlign: 'middle', mr: 2 }} />
          Toast Animation Test Lab
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Fine-tune toast animations with real-time preview
        </Typography>
      </PageHeader>

      <Grid container spacing={3}>
        {/* Left Column - Controls */}
        <Grid item xs={12} md={5}>
          {/* Toast Type Selection */}
          <ControlCard>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Toast Type
              </Typography>
              <ButtonGroup fullWidth variant="outlined" sx={{ mb: 2 }}>
                <Button
                  variant={toastType === 'success' ? 'contained' : 'outlined'}
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => setToastType('success')}
                >
                  Success
                </Button>
                <Button
                  variant={toastType === 'error' ? 'contained' : 'outlined'}
                  color="error"
                  startIcon={<ErrorIcon />}
                  onClick={() => setToastType('error')}
                >
                  Error
                </Button>
              </ButtonGroup>

              <Divider sx={{ my: 2 }} />

              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Animation Controls
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={infiniteLoop}
                    onChange={(e) => setInfiniteLoop(e.target.checked)}
                    color="primary"
                  />
                }
                label="Infinite Loop Mode"
              />
              {infiniteLoop && (
                <Chip
                  label={`Loop Count: ${loopCount}`}
                  color="primary"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={infiniteLoop ? <Pause /> : <PlayArrow />}
                  onClick={() => triggerToast()}
                  sx={{ mb: 1 }}
                >
                  {infiniteLoop ? 'Playing' : 'Trigger Toast'}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Refresh />}
                  onClick={resetToDefaults}
                >
                  Reset to Defaults
                </Button>
              </Box>
            </CardContent>
          </ControlCard>

          {/* Current Stats */}
          <StatsCard>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Current Animation Stats
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Time: <strong>{formatTime(totalTime)}</strong>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {formatTimeBreakdown(timing)}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Type: <strong>{toastType}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Message: <strong>{toastMessage || '(no text)'}</strong>
            </Typography>
          </StatsCard>
        </Grid>

        {/* Right Column - Test Messages */}
        <Grid item xs={12} md={7}>
          <ControlCard>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Test Messages
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Toast will appear centered on screen when triggered
              </Typography>

              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Quick Test Messages
              </Typography>
              <Grid container spacing={1}>
                {/* No Text Button */}
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={() => {
                      setToastMessage('');
                      triggerToast('');
                    }}
                    sx={{ fontWeight: 600 }}
                  >
                    No Text
                  </Button>
                </Grid>
                {(toastType === 'success'
                  ? SAMPLE_MESSAGES
                  : SAMPLE_ERROR_MESSAGES
                )
                  .slice(0, 5)
                  .map((msg: string, idx: number) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => triggerToast(msg)}
                      >
                        {msg.length > 20 ? msg.substring(0, 20) + '...' : msg}
                      </Button>
                    </Grid>
                  ))}
              </Grid>

              <TextField
                fullWidth
                size="small"
                label="Custom Message"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                sx={{ mt: 2, mb: 1 }}
              />
              <Button
                variant="outlined"
                fullWidth
                disabled={!customMessage}
                onClick={() => {
                  triggerToast(customMessage);
                  setCustomMessage('');
                }}
              >
                Use Custom Message
              </Button>
            </CardContent>
          </ControlCard>
        </Grid>
      </Grid>

      {/* Toast Component - using current implementation */}
      <ToastMessage
        show={showToast}
        type={toastType}
        message={toastMessage}
        onClose={handleToastClose}
      />
    </PageContainer>
  );
};

export default ToastTestPage;
